import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Dialog } from "@mui/material";
import AlertDialog from "../../components/shared/AlertDialog";
import { VStack } from "../../components/utils";
import {
  getPlanDocuments,
  uploadPlanDocuments,
  updatePlanDocument,
  deletePlanDocuments,
  IPlanDocument,
} from "../../api/planDocumentsApi";
import { DocumentType } from "../../types/DocumentsInterface";
import { PlanDocumentList } from "./components/PlanDocumentList";
import { PlanDocumentCreate } from "./components/PlanDocumentCreate";
import { convertFileToBase64 } from "../../utils/DownloadFile";

interface PlanDocumentsDialogProps {
  plan: { planName: string; esopPlanId: string };
  onClose: () => void;
  onViewChange?: (view: "list" | "create") => void;
}
const DOCUMENT_TYPES = [
  { label: "ESOP Plan Document", value: DocumentType.ESOP_PLAN_DOCUMENT },
  { label: "ESOP Board Document", value: DocumentType.ESOP_BOARD_DOCUMENT },
  { label: "Others", value: DocumentType.OTHERS },
];

export default function PlanDocumentsDialog({
  plan,
  onClose,
  onViewChange,
}: PlanDocumentsDialogProps) {
  const [view, setView] = useState<"list" | "create">("list");
  const [documents, setDocuments] = useState<IPlanDocument[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<IPlanDocument | null>(null);
  const [editingDoc, setEditingDoc] = useState<IPlanDocument | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    docId?: string;
  }>({
    open: false,
    docId: undefined,
  });
  const [accordionOpened, setAccordionOpened] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    if (onViewChange) {
      onViewChange(view);
    }
  }, [view, onViewChange]);

  const fetchDocuments = useCallback(async () => {
    setIsLoading(true);
    try {
      const docs = await getPlanDocuments(plan.esopPlanId);
      setDocuments(docs || []);
      const uniqueTypes = DOCUMENT_TYPES.map((t) => t.value);
      const initialOpenState = uniqueTypes.reduce(
        (acc, curr) => ({ ...acc, [curr]: true }),
        {} as Record<string, boolean>
      );
      setAccordionOpened((prev) => ({ ...prev, ...initialOpenState }));
    } catch (error) {
      toast.error("Failed to fetch documents");
    } finally {
      setIsLoading(false);
    }
  }, [plan.esopPlanId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDelete = (docId: string | undefined) => {
    if (!docId) return;
    setDeleteDialog({ open: true, docId });
  };

  const confirmDelete = async () => {
    const docId = deleteDialog.docId;
    if (!docId) return;
    setIsLoading(true);
    try {
      await deletePlanDocuments([docId]);
      toast.success("Document deleted successfully");
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      if (selectedDoc?.id === docId) setSelectedDoc(null);
      setDeleteDialog({ open: false, docId: undefined });
    } catch (error) {
      toast.error("Failed to delete document");
    } finally {
      setIsLoading(false);
    }
  };

  const uploadFormik = useFormik({
    initialValues: {
      name: "",
      documentType: "",
      shownToEmployee: false,
      canEmployeeDownload: false,
      tags: [] as { label: string; value: string }[],
      file: null as File | null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Document Name is required"),
      documentType: Yup.string().required("Document Type is required"),
      file: editingDoc ? Yup.mixed() : Yup.mixed().required("File is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsLoading(true);

      try {
        if (editingDoc) {
          const payload = {
            id: editingDoc.id!,
            documentType: values.documentType,
            documentName: values.name,
            metaData: {
              shownToEmployee: values.shownToEmployee,
              canEmployeeDownload: values.shownToEmployee
                ? values.canEmployeeDownload
                : false,
              tags: values.tags || [],
            },
          };
          await updatePlanDocument(payload);
          toast.success("Document updated successfully!");
        } else {
          if (!values.file) return;
          const base64String = await convertFileToBase64(values.file);
          const base64Data =
            (base64String as string).split(",")[1] || (base64String as string);

          const payload = {
            entityId: plan.esopPlanId,
            documentType: values.documentType,
            documentName: values.name,
            b64File: base64Data,
            contentType: values.file.type,
            metaData: {
              shownToEmployee: values.shownToEmployee,
              canEmployeeDownload: values.canEmployeeDownload,
              tags: values.tags || [],
            },
          };

          await uploadPlanDocuments(payload);
          toast.success("Document uploaded successfully!");
        }
        resetForm();
        setEditingDoc(null);
        setView("list");
        fetchDocuments();
      } catch (err) {
        toast.error("Failed to upload document");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleAccordionClick = (section: string) => {
    setAccordionOpened((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <VStack className="flex w-full h-[700px] bg-white rounded-xl shadow-lg overflow-hidden">
      <VStack className="flex flex-col w-full border-b border-gray-200">
        <div className="p-6 pb-2">
          <div className="flex justify-between items-center mb-4">
            <h6 className="text-xl font-medium text-gray-800">
              Plan Documents - {plan.planName}
            </h6>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <Icon icon="mdi:close" width={24} />
            </button>
          </div>

          <div className="flex gap-6 text-sm font-medium">
            <button
              onClick={() => {
                setView("list");
                setEditingDoc(null);
                uploadFormik.resetForm();
              }}
              className={`pb-2 border-b-2 transition-colors ${
                view === "list"
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Plan Docs
            </button>
            <button
              onClick={() => {
                setView("create");
                setSelectedDoc(null);
                setEditingDoc(null);
                uploadFormik.resetForm();
              }}
              className={`pb-2 border-b-2 transition-colors ${
                view === "create"
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {editingDoc ? "Edit Document" : "Create Document"}
            </button>
          </div>
        </div>
      </VStack>

      <div className="flex-1 min-h-0 relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        )}

        {view === "list" ? (
          <PlanDocumentList
            documents={documents}
            selectedDoc={selectedDoc}
            setSelectedDoc={setSelectedDoc}
            accordionOpened={accordionOpened}
            handleAccordionClick={handleAccordionClick}
            handleDelete={handleDelete}
            handleEdit={(doc) => {
              setEditingDoc(doc);
              uploadFormik.setValues({
                name: doc.documentName || "",
                documentType: doc.documentType || "",
                shownToEmployee: doc.metaData?.shownToEmployee || false,
                canEmployeeDownload: doc.metaData?.canEmployeeDownload || false,
                tags: doc.metaData?.tags || [],
                file: null,
              });
              setView("create");
            }}
            DOCUMENT_TYPES={DOCUMENT_TYPES}
          />
        ) : (
          <PlanDocumentCreate
            uploadFormik={uploadFormik}
            DOCUMENT_TYPES={DOCUMENT_TYPES}
            isLoading={isLoading}
            onCancel={() => {
              setView("list");
              setEditingDoc(null);
              uploadFormik.resetForm();
            }}
            isEditMode={!!editingDoc}
            existingFileName={editingDoc?.documentName}
          />
        )}
      </div>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, docId: undefined })}
        maxWidth="md"
      >
        <AlertDialog
          onClose={() => setDeleteDialog({ open: false, docId: undefined })}
          open={deleteDialog.open}
          message={"Do you want to delete this document?"}
          onPrimaryAction={confirmDelete}
          onSecondaryAction={() =>
            setDeleteDialog({ open: false, docId: undefined })
          }
        />
      </Dialog>
    </VStack>
  );
}
